import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Rating } from './useRatings';

export interface RatingArchive {
  id: string;
  title: string;
  votingType: 'team' | 'individual';
  archivedAt: number;
  totalVoters: number;
  ratingsSnapshot: Rating[];
}

function dbRowToArchive(row: Record<string, unknown>): RatingArchive {
  return {
    id: row.id as string,
    title: (row.title as string) ?? '',
    votingType: (row.voting_type as 'team' | 'individual') ?? 'individual',
    archivedAt: row.archived_at as number,
    totalVoters: row.total_voters as number,
    ratingsSnapshot: (row.ratings_snapshot as Rating[]) ?? [],
  };
}

export function useRatingArchives() {
  const [archives, setArchives] = useState<RatingArchive[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  const fetchArchives = useCallback(async () => {
    const { data } = await supabase
      .from('rating_archives')
      .select('*')
      .order('archived_at', { ascending: false });
    if (data) setArchives(data.map(r => dbRowToArchive(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchArchives();
    const channel = supabase
      .channel(`rating-archives-rt-${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rating_archives' }, () => {
        fetchArchives();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchArchives]);

  const archiveSession = useCallback(async (
    title: string,
    votingType: 'team' | 'individual',
    ratings: Rating[],
  ) => {
    if (ratings.length === 0) return;
    setSaving(true);
    const now = Date.now();
    const totalVoters = new Set(ratings.map(r => r.raterId)).size;
    const row = {
      id: `archive-${now}`,
      title: title || `評分封存 ${new Date(now).toLocaleDateString('zh-TW')}`,
      voting_type: votingType,
      archived_at: now,
      total_voters: totalVoters,
      ratings_snapshot: ratings,
    };
    await supabase.from('rating_archives').insert(row);

    // Immediately update local state so UI reflects the new archive right away
    const newArchive: RatingArchive = {
      id: row.id,
      title: row.title,
      votingType: row.voting_type,
      archivedAt: row.archived_at,
      totalVoters: row.total_voters,
      ratingsSnapshot: ratings,
    };
    setArchives(prev => [newArchive, ...prev]);

    setSaving(false);
  }, []);

  const deleteArchive = useCallback(async (id: string) => {
    setArchives(prev => prev.filter(a => a.id !== id));
    await supabase.from('rating_archives').delete().eq('id', id);
  }, []);

  const clearAllArchives = useCallback(async () => {
    setArchives([]);
    await supabase.from('rating_archives').delete().not('id', 'is', null);
  }, []);

  return { archives, loading, saving, archiveSession, deleteArchive, clearAllArchives };
}
